import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { DprActivityGridData } from '@dpr-details/models/grid';
import { StaticMessage, UtcDateTimeOptions } from '@shared/enums';
import { DateFilter, SelectedSubproject, SelectionItem } from '@shared/models';
import { DprDetails } from '@shared/models/dpr';
import { ActivityStream } from '@shared/models/project-setup';
import { filterAvailableDates, toUTCDate } from '@shared/utils';

@Component({
  selector: 'app-dpr-copy-activity-dialog-edit',
  templateUrl: './dpr-copy-activity-dialog-edit.component.html',
  styleUrls: ['./dpr-copy-activity-dialog-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprCopyActivityDialogEditComponent implements OnInit {
  startDate: Date | null = null;
  minDate: Date | null = null;
  maxDate: Date | null = null;
  selectedDate = new FormControl<Date | null>(null);

  activityStreams: SelectionItem[];
  selectedActivityStreams: (number | string)[];

  customData: SelectionItem[] = [];
  selectedCustomData: (number | string)[];

  StaticMessage = StaticMessage;

  selectActivityStreamsLabel = $localize`:@@dprDetails.dprCopyActivityDialog.lblSelectDestinationActivityStreams:Select destination activity stream(s)`;
  selectCustomDataLabel = $localize`:@@dprDetails.dprCopyActivityDialog.lblSelectCustomDataToCopy:Select custom data to copy`;

  constructor(
    public dialogRef: MatDialogRef<DprCopyActivityDialogEditComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      dprDetails: DprDetails;
      subproject: SelectedSubproject;
      selectedSavedActivities: DprActivityGridData[];
      canEdit: boolean;
      availableDates: DateFilter;
      defaultActivityStream: ActivityStream;
    }
  ) {}

  ngOnInit(): void {
    this.startDate = toUTCDate(
      this.data.dprDetails.dprDateTime,
      UtcDateTimeOptions.UtcDateWithoutTime
    );

    this.minDate = toUTCDate(
      this.data.availableDates.firstDate,
      UtcDateTimeOptions.UtcDateWithoutTime
    );
    this.maxDate = toUTCDate(
      this.data.availableDates.lastDate,
      UtcDateTimeOptions.UtcDateWithoutTime
    );

    this.selectedDate.setValue(
      !this.data.dprDetails.lockedDate ? this.startDate : null
    );

    this.initializeActivityStreams();
    this.initializeCustomData();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onCopy(): void {
    const date = toUTCDate(
      this.selectedDate.value,
      UtcDateTimeOptions.UtcDateWithoutTimeZone
    );

    this.data.selectedSavedActivities.forEach((dprActivity) => {
      const customDataIds = this.selectedCustomData
        .filter((cd) => +cd.toString().split('_')[1] === dprActivity.activityId)
        .map((cd) => +cd.toString().split('_')[0]);

      dprActivity.syncDprActivityCustomDataList = {
        addedList: dprActivity.dprActivityCustomDataList.filter((dprCd) =>
          customDataIds.includes(dprCd.customDataId)
        ),
        deletedIds: [],
        updatedList: [],
      };
    });

    this.dialogRef.close({
      selectedActivities: this.data.selectedSavedActivities,
      destinationDprDate: date,
      destinationActivityStreams: this.selectedActivityStreams,
      subprojectId: this.data?.subproject.id,
      dprDetailId: this.data?.dprDetails.id,
    });
  }

  onSelectionChanged(data: (number | string)[]): void {
    this.selectedActivityStreams = data;
  }

  onCustomDataSelectionChanged(data: (number | string)[]): void {
    this.selectedCustomData = data;
  }

  filterAvailableDates = (date: Date): boolean => {
    return filterAvailableDates(date || new Date(), this.data?.availableDates);
  };

  private initializeActivityStreams(): void {
    this.activityStreams = (
      this.getActivityStreams(
        this.data.subproject,
        this.data.selectedSavedActivities
      ) ?? []
    ).map(
      (ast): SelectionItem => ({
        id: ast.id,
        name: ast.value,
      })
    );

    this.selectedActivityStreams = [this.data?.defaultActivityStream.id];
  }

  private getActivityStreams(
    subproject: SelectedSubproject,
    selectedRows: DprActivityGridData[]
  ): ActivityStream[] {
    const commonActivityStreamIds = selectedRows
      // Extract the activity stream IDs for each selected row
      .map(
        (row) =>
          subproject.data?.activityGroupList
            ?.find((ag) => ag.id === row.activityGroupId)
            ?.activityGroupActivityStreamList?.map(
              (ast) => ast.activityStreamId
            ) || []
      )
      // Find the common activity stream IDs across all selected rows
      .reduce(
        (acc, ids) =>
          acc.length === 0 ? ids : acc.filter((id) => ids.includes(id)),
        []
      );

    // Filter the activity streams to return only the active ones with common IDs
    return (
      subproject.data?.activityStreams.filter(
        (ast) => commonActivityStreamIds.includes(ast.id) && ast.isActive
      ) || []
    );
  }

  private initializeCustomData(): void {
    // Filtering custom data ids to return those custom data which are assigned to the selected activities
    this.selectedCustomData =
      this.data?.subproject?.data?.activityMobilisationList
        ?.filter(
          (am, index, list) =>
            this.data?.selectedSavedActivities
              ?.map((row) => row.activityId)
              .includes(am.activityId) &&
            // this filters the entities with same activityId and customDataId but with different parallelActivityType
            index ===
              list.indexOf(
                list.find(
                  (el) =>
                    el.activityId === am.activityId &&
                    el.customDataId === am.customDataId
                )
              )
        )
        ?.sort((a, b) => a.activityId - b.activityId)
        .map((am) => {
          const customDataName =
            this.data?.subproject?.data?.customDataList.find(
              (cd) => cd.id === am.customDataId
            )?.name;
          const activityName = this.data?.subproject?.data?.activityList.find(
            (a) => a.id === am.activityId
          )?.name;
          this.customData.push({
            id: `${am.customDataId}_${am.activityId}`,
            name: `${customDataName} (${activityName})`,
          });

          // concatenating customDataId and activityId in order to distinguish custom data
          return `${am.customDataId}_${am.activityId}`;
        });
  }
}
